import { useState, useEffect, useCallback } from 'react';
import './TurnTimer.css';

export default function TurnTimer({
  turnEndTime,
  isYourTurn,
  currentPlayerName,
  onTimeWarning,
  onTimeExpired
}) {
  const [timeLeft, setTimeLeft] = useState(null);
  const [warningShown, setWarningShown] = useState(false);

  // Calculate time left
  const calculateTimeLeft = useCallback(() => {
    if (!turnEndTime) return null;

    const now = new Date();
    const end = new Date(turnEndTime);
    const diff = Math.max(0, Math.floor((end - now) / 1000));

    return diff;
  }, [turnEndTime]);

  // Update timer every second
  useEffect(() => {
    if (!turnEndTime) {
      setTimeLeft(null);
      return;
    }

    // Initial calculation
    setTimeLeft(calculateTimeLeft());
    setWarningShown(false);

    const interval = setInterval(() => {
      const remaining = calculateTimeLeft();
      setTimeLeft(remaining);

      // 30 second warning
      if (remaining === 30 && !warningShown && isYourTurn) {
        setWarningShown(true);
        onTimeWarning?.();
      }

      // Time expired
      if (remaining === 0) {
        onTimeExpired?.();
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [turnEndTime, calculateTimeLeft, isYourTurn, onTimeWarning, onTimeExpired, warningShown]);

  // Format time display
  const formatTime = (seconds) => {
    if (seconds === null) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Determine timer state classes
  const getTimerClass = () => {
    if (timeLeft === null) return '';
    if (timeLeft <= 30) return 'critical';
    if (timeLeft <= 60) return 'warning';
    return '';
  };

  if (!turnEndTime) return null;

  return (
    <div className={`turn-timer ${getTimerClass()} ${isYourTurn ? 'your-turn' : ''}`}>
      <div className="timer-content">
        <div className="timer-label">
          {isYourTurn ? (
            <>
              <span className="your-turn-text">Your Turn</span>
            </>
          ) : (
            <>
              <span className="player-turn-text">{currentPlayerName}'s Turn</span>
            </>
          )}
        </div>
        <div className="timer-countdown">
          <svg className="timer-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
          </svg>
          <span className="timer-value">{formatTime(timeLeft)}</span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="timer-progress">
        <div
          className="timer-progress-fill"
          style={{
            width: `${(timeLeft / 180) * 100}%` // 180 seconds = 3 minutes
          }}
        />
      </div>
    </div>
  );
}
